import {
  Box,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import clsx from 'clsx';
import { Children, Fragment, isValidElement, ReactNode, useState } from 'react';
import { WbDivider } from '../WbDivider';
import { WbPopover } from '../WbPopover';
import { MainSeparator } from './separators/MainSeparator';
import { SecondarySeparator } from './separators/SecondarySeparator';
import { Separator, Variant } from './types';
import { useResponsiveMobile } from '../../hooks';
import { WbMobileBreadcrumbMenu } from './WbMobileBreadcrumbMenu';

type WbBreadcrumbsProps = {
  children: ReactNode;
  variant?: Variant;
  className?: string;
  disableLastItemHighlight?: boolean;
  showEllipsis?: boolean;
  hideFirstElement?: boolean;
};
const useStyles = makeStyles<Theme>(
  theme => ({
    root: {
      '& ul': {
        listStyle: 'none',
        display: 'flex',
        height: '100%',
        margin: 0,
        padding: 0,
      },
    },
    main: {
      height: '100%',
      backgroundColor: theme.palette.white,

      '& $firstItem': {
        paddingLeft: '16px',
        backgroundColor: theme.palette.bkg.secondary,
      },
      '& $lastItem': {
        fontWeight: theme.typography.fontWeightMedium as number,
        color: theme.palette.secondary.dark,
      },
      '& $itemContainer': {
        paddingInline: '16px',
      },
    },
    secondary: {
      height: '18px',
      '& > ul': {
        gap: theme.spacing(1),
      },
      '& $lastItem': {
        color: theme.palette.primary.main,
      },
      '& $firstItem': {},
    },
    item: {
      height: '100%',
      color: theme.palette.secondary.main,
      ...theme.typography.body2,
    },
    firstItem: {},
    lastItem: {},
    itemContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    ellipsis: {
      whiteSpace: 'nowrap',
      color: theme.palette.text.secondary,
    },
    breadcrumbRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    popoverContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      marginTop: '8px',
    },
    smallIcon: {
      width: '12px',
      height: '12px',
    },
    ellipsisContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
    },
  }),
  { name: 'WbBreadcrumbs' },
);

const WbBreadcrumbWrapper = ({
  children,
  i,
  last,
  disableLastItemHighlight,
}: {
  children?: ReactNode;
  variant: Variant;
  i: number;
  last?: boolean;
  disableLastItemHighlight?: boolean;
}) => {
  const classes = useStyles();

  return (
    <Box
      component="li"
      className={clsx(
        classes.itemContainer,
        classes.item,
        i === 0 && classes.firstItem,
        last && !disableLastItemHighlight && classes.lastItem,
      )}
    >
      {children}
    </Box>
  );
};

const separators: Record<Variant, Separator> = {
  main: MainSeparator,
  secondary: SecondarySeparator,
};

const insertSeparators = (
  items: JSX.Element[],
  variant: Variant,
  insertAtTheEnd = false,
) => {
  const SeparatorComponent = separators[variant];

  return items.reduce<JSX.Element[]>((acc, current, index) => {
    let next = acc;

    if (index < items.length - 1 || insertAtTheEnd) {
      next = next.concat(
        current,
        <SeparatorComponent i={index} key={`separator-${index}`} />,
      );
    } else {
      next.push(current);
    }

    return next;
  }, []);
};
const insertEllipsedSeparators = (
  items: JSX.Element[],
  variant: Variant,
  insertAtTheEnd = false,
) => {
  const SeparatorComponent = separators[variant];

  return items.reduce<JSX.Element[]>((acc, current, index) => {
    let next = acc;

    if (index < items.length - 1 || insertAtTheEnd) {
      next = next.concat(
        current,
        <SeparatorComponent i={index + 1} key={`separator-${index}`} />,
      );
    } else {
      next.push(current);
    }

    return next;
  }, []);
};

const EllipsisBreadcrumb = ({ children }: { children: JSX.Element[] }) => {
  const classes = useStyles();
  const [showEllipsedItems, setShowEllipsedItems] = useState(false);

  const handleMouseEnter = () => {
    setShowEllipsedItems(true);
  };

  const handleMouseLeave = () => {
    setShowEllipsedItems(false);
  };

  const finalItems = insertEllipsedSeparators(children, 'main', false);

  return (
    <WbBreadcrumbWrapper
      disableLastItemHighlight
      variant="main"
      i={999}
      last={false}
    >
      <Box
        className={classes.ellipsisContainer}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showEllipsedItems ? (
          <Box component="ul">{finalItems}</Box>
        ) : (
          <Box component="span">...</Box>
        )}
      </Box>
    </WbBreadcrumbWrapper>
  );
};

export const WbBreadcrumbs = ({
  children,
  className,
  variant: breadcrumbVariant = 'main',
  disableLastItemHighlight: disableHighlightLastItem,
  showEllipsis: showEllipsisIndicator = false,
  hideFirstElement: hideFirstBreadcrumb = false,
}: WbBreadcrumbsProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useResponsiveMobile();

  const breadcrumbElements = Children.toArray(children);
  const validBreadcrumbElements: JSX.Element[] = breadcrumbElements
    .filter(child => isValidElement(child))
    .map(child => child as JSX.Element);

  let filteredBreadcrumbs: JSX.Element[] = validBreadcrumbElements;

  const numBreadcrumbsToDisplayAtStart = 3;
  const numBreadcrumbsToDisplayAtEnd = 2;

  if (breadcrumbVariant === 'secondary') {
    const ellipsisIndicator = <Box className={classes.ellipsis}>...</Box>;

    if (showEllipsisIndicator) {
      if (hideFirstBreadcrumb && validBreadcrumbElements.length > 1) {
        // Show: ... > last
        filteredBreadcrumbs = [
          ellipsisIndicator,
          validBreadcrumbElements[validBreadcrumbElements.length - 1],
        ];
      } else if (validBreadcrumbElements.length > 2) {
        // Show: first > ... > last
        filteredBreadcrumbs = [
          validBreadcrumbElements[0],
          ellipsisIndicator,
          validBreadcrumbElements[validBreadcrumbElements.length - 1],
        ];
      }
    }
  } else {
    // Main variant
    if (isMobile && filteredBreadcrumbs.length > 2) {
      const collapsedBreadcrumbs = filteredBreadcrumbs.slice(
        0,
        filteredBreadcrumbs.length - 1,
      );
      const lastBreadcrumb =
        filteredBreadcrumbs[filteredBreadcrumbs.length - 1];
      const finalBreadcrumbs = insertSeparators(
        [
          <WbMobileBreadcrumbMenu>
            {collapsedBreadcrumbs}
          </WbMobileBreadcrumbMenu>,
          <WbBreadcrumbWrapper
            disableLastItemHighlight={disableHighlightLastItem}
            variant={breadcrumbVariant}
            key={filteredBreadcrumbs.length}
            i={filteredBreadcrumbs.length}
            last
          >
            {lastBreadcrumb}
          </WbBreadcrumbWrapper>,
        ],
        'main',
        false,
      );

      return (
        <>
          <Box
            component="nav"
            className={clsx(
              classes.root,
              classes[breadcrumbVariant],
              className,
            )}
          >
            <Box component="ul">{finalBreadcrumbs}</Box>
          </Box>
        </>
      );
    }
    if (
      filteredBreadcrumbs.length >
      numBreadcrumbsToDisplayAtStart + numBreadcrumbsToDisplayAtEnd
    ) {
      const wrappedBreadcrumbs = filteredBreadcrumbs.map((child, index) => (
        <WbBreadcrumbWrapper
          disableLastItemHighlight={disableHighlightLastItem}
          variant={breadcrumbVariant}
          key={index}
          i={index}
          last={index === filteredBreadcrumbs.length - 1}
        >
          {child}
        </WbBreadcrumbWrapper>
      ));

      const startBreadcrumbs = wrappedBreadcrumbs.slice(
        0,
        numBreadcrumbsToDisplayAtStart,
      );
      const ellipsedBreadcrumbs = wrappedBreadcrumbs.slice(
        numBreadcrumbsToDisplayAtStart,
        filteredBreadcrumbs.length - numBreadcrumbsToDisplayAtEnd,
      );
      const endBreadcrumbs = wrappedBreadcrumbs.slice(
        filteredBreadcrumbs.length - numBreadcrumbsToDisplayAtEnd,
      );

      filteredBreadcrumbs = [
        ...startBreadcrumbs,
        <EllipsisBreadcrumb key="ellipsis-breadcrumb">
          {ellipsedBreadcrumbs}
        </EllipsisBreadcrumb>,
        ...endBreadcrumbs,
      ];

      const insertSeparatorAtTheEnd =
        breadcrumbVariant === 'main' && filteredBreadcrumbs.length === 1;
      const finalBreadcrumbs = insertSeparators(
        filteredBreadcrumbs,
        breadcrumbVariant,
        insertSeparatorAtTheEnd,
      );

      return (
        <>
          <Box
            component="nav"
            className={clsx(
              classes.root,
              classes[breadcrumbVariant],
              className,
            )}
          >
            <Box component="ul">{finalBreadcrumbs}</Box>
          </Box>
        </>
      );
    }
  }

  const allBreadcrumbs = filteredBreadcrumbs.map((child, index) => (
    <WbBreadcrumbWrapper
      disableLastItemHighlight={disableHighlightLastItem}
      variant={breadcrumbVariant}
      key={index}
      i={index}
      last={index === filteredBreadcrumbs.length - 1}
    >
      {child}
    </WbBreadcrumbWrapper>
  ));

  const insertSeparatorAtTheEnd =
    breadcrumbVariant === 'main' && allBreadcrumbs.length === 1;
  const finalBreadcrumbs = insertSeparators(
    allBreadcrumbs,
    breadcrumbVariant,
    insertSeparatorAtTheEnd,
  );

  if (breadcrumbVariant === 'secondary' && validBreadcrumbElements.length > 0) {
    return (
      <WbPopover
        enterDelay={300}
        enterNextDelay={300}
        component={
          <Box padding={2}>
            {validBreadcrumbElements.length > 1 && (
              <Typography
                variant="overline"
                style={{ color: theme.palette.black }}
              >
                Full Domain Path
              </Typography>
            )}

            <Box className={classes.popoverContainer}>
              {validBreadcrumbElements.map((child, index) => (
                <Fragment key={`child-${index}`}>
                  <Box className={classes.breadcrumbRow}>
                    {index === 0 ||
                    index === validBreadcrumbElements.length - 1 ? (
                      <FiberManualRecordIcon className={classes.smallIcon} />
                    ) : (
                      <ArrowForwardIosIcon className={classes.smallIcon} />
                    )}
                    {child}
                  </Box>
                  {index !== validBreadcrumbElements.length - 1 && (
                    <WbDivider
                      orientation="vertical"
                      height="8px"
                      style={{ marginLeft: '8px' }}
                    />
                  )}
                </Fragment>
              ))}
            </Box>
          </Box>
        }
      >
        <Box
          component="nav"
          className={clsx(classes.root, classes[breadcrumbVariant], className)}
        >
          <Box component="ul">{finalBreadcrumbs}</Box>
        </Box>
      </WbPopover>
    );
  }

  return (
    <>
      <Box
        component="nav"
        className={clsx(classes.root, classes[breadcrumbVariant], className)}
      >
        <Box component="ul">{finalBreadcrumbs}</Box>
      </Box>
    </>
  );
};
